import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pt } from 'date-fns/locale';

import { Alerta } from '../../components/alerta';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import {
    formatIsoDate,
    formatMonthYearLabelPtBr,
    getMonthYearDatePickerProps
} from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/billingCsv.css';

const GerarCSVFaturamento = () => {
    const [dateTime, setDateTime] = useState(null);
    const [error, setError] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    async function submitAtualizarDoacao (e) {
        e.preventDefault();
        setIsSubmit(true);

        let periodo = '';
        if (dateTime) {
            setError(false);
            periodo = formatIsoDate(dateTime);
        } else {
            setError(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({ periodo });
        try {
            const response = await fetchFlask('gerarCSVFaturamento', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
            if (response.status === 'success') {
                setDateTime(null);
            }
        } finally {
            setIsSubmit(false);
        }
    };

    const competenciaLabel = formatMonthYearLabelPtBr(dateTime, 'Pendente');
    const periodoPayloadLabel = dateTime ? formatIsoDate(dateTime) : 'Não gerado';
    const completedFields = [dateTime].filter(Boolean).length;

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Gerar CSV Faturamento
            </PageHeader>

            <form action='' method='POST' id='formDownloadFatura' onSubmit={submitAtualizarDoacao} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Cálculo do faturamento</h2>
                        </Box>
                        <p>
                            Selecione a competência de faturamento para gerar o CSV na pasta configurada pelo processo.
                        </p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <CalendarMonthRoundedIcon />
                            <Box>
                                <span>Etapa única</span>
                                <strong>Competência do CSV</strong>
                            </Box>
                        </Box>

                        <Box className='billing-csv-period-strip'>
                            <TableChartRoundedIcon />
                            <FormControl className='ui-control' error={error}>
                                <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                                    <DatePicker id='data' label='Período'
                                        {...getMonthYearDatePickerProps(setDateTime)}
                                        minDate={new Date('2023-01-02')}
                                        maxDate={new Date()}
                                        value={dateTime}
                                        slotProps={{
                                            openPickerButton: {
                                                color: 'primary'
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                {error && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='ui-impact'>
                        <FolderRoundedIcon />
                        <Box>
                            <strong>Arquivo salvo na pasta de contas</strong>
                            <span>O envio solicita a geração do CSV de faturamento para a competência selecionada.</span>
                        </Box>
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da geração</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Competência</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{competenciaLabel}</strong>
                        </Box>
                        <Box>
                            <span>Período enviado</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{periodoPayloadLabel}</strong>
                        </Box>
                        <Box>
                            <span>Saída</span>
                            <strong>CSV enviado por e-mail</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/1 preenchido</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<TableChartRoundedIcon />}>
                        Gerar CSV
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default GerarCSVFaturamento;
